import React, { forwardRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, SizeProp, IconDefinition, IconPrefix, findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { IFAIconProps, TSize, TIconColor, TIconStyle } from './types';
import { formatClassName } from '../../utils';
import '../../icons/library';

const prefixes: Record<string, IconPrefix> = {
  solid: 'fas',
  regular: 'far',
  light: 'fal',
  brands: 'fab',
  thin: 'fat',
};

const getIconFromLibrary: (
  icon: IconName,
  additionalProps?: { style: TIconStyle },
  style?: TIconStyle
) => IconDefinition = (icon, additionalProps, style) => {
  const prefix = (additionalProps && prefixes[additionalProps?.style]) || prefixes[style as IconPrefix];
  const definition = findIconDefinition({ prefix, iconName: icon }); // check for default styling or if explicitly set

  if (!definition) {
    for (const value of Object.values(prefixes)) {
      const altDefinition = findIconDefinition({ prefix: value, iconName: icon });

      if (altDefinition) return altDefinition;
    }
  }
  return definition;
};

export const FAIcon = forwardRef<SVGSVGElement, IFAIconProps>((props, ref) => {
  const {
    additionalClasses = '',
    ariaLabel,
    size,
    style,
    color = '',
    maskIcon = '', // Mask/layer will be the background so main icon needs to be transformed
    maskProps, // additional props for the mask icon
    layerIcon = '',
    layerProps, // additional props for the layer icon
    onClick,
    rotation,
    spin,
    transform,
  } = props;
  const [colorClass, setColorClass] = useState<TIconColor | ''>(color);
  const [extraClasses, setExtraClasses] = useState<string>(additionalClasses);

  useEffect(() => {
    setColorClass(color);
  }, [color]);

  useEffect(() => {
    setExtraClasses(additionalClasses);
  }, [additionalClasses]);

  const mask = maskIcon && getIconFromLibrary(maskIcon, maskProps, style);
  const layer = layerIcon && getIconFromLibrary(layerIcon, layerProps, style);
  const icon = props.style ? getIconFromLibrary(props.icon, { style: props.style }) : getIconFromLibrary(props.icon);

  const [iconSize, setIconSize] = useState<SizeProp>();

  useEffect(() => {
    if (size) {
      const mappedSize: Record<TSize, SizeProp | undefined> = {
        small: undefined,
        medium: '2x',
        large: '3x',
        extralarge: '4x',
      };

      setIconSize(mappedSize[size]);
    }
  }, [size]);

  if (!icon) return <></>;

  const formattedAdditionalClasses = formatClassName([colorClass, extraClasses, 'fa-icon', 'fa-fw']);

  const commonProps = {
    spin,
    rotation,
    transform,
    onClick,
    size: iconSize,
    className: `${layerIcon && 'fa-inverse'} ${formattedAdditionalClasses}`,
    'aria-label': ariaLabel,
    'data-action': `fa-${icon.iconName}`,
  };

  if (layerIcon && layer) {
    return (
      // TODO: Remove this any after fixing the ref type
      <span className={`fa-layers fa-fw fa-${iconSize}`} ref={ref as any}>
        <FontAwesomeIcon icon={icon} {...commonProps} />

        <FontAwesomeIcon
          icon={layer}
          className={formattedAdditionalClasses}
          rotation={layerProps?.rotation}
          spin={layerProps?.spin}
          size={iconSize}
        />
      </span>
    );
  }

  return <FontAwesomeIcon ref={ref} icon={icon} mask={maskIcon && mask ? mask : undefined} {...commonProps} />;
});

FAIcon.displayName = 'FAIcon';
